import React, { useCallback, useEffect, useState } from 'react'

import { twMerge } from 'tailwind-merge'

import useDisclosure from 'src/hooks/use-disclosure'

import VStack from '../Flex/VStack'
import Icon from '../Icon/Icon'
import { IconKey } from '../Icon/util'
import Input from '../Input/Input'
import Popover from '../Popover/Popover'

import { ColumnHeaderActionsConfig } from './headerActions'

/**
 * Popover trigger + content rendered next to the header label when
 * `headerActions` is provided. The chevron is always visible so the
 * affordance reads as "this column is configurable" without hover
 * discovery; the rows inside are gated on the `can*` flags.
 *
 * Click on the chevron stops propagation so it doesn't bubble into
 * `header.column.getToggleSortingHandler()` on the cell.
 */
export const PER_COLUMN_RENAME_MAX_CHARS = 30

/**
 * Letters, numbers, marks, and spaces — bans punctuation, symbols, and
 * emoji. `\p{L}` / `\p{N}` / `\p{M}` keep accented letters (ñ, é, ü, …)
 * and non-Latin scripts intact; the goal is to block `!`, `,`, emoji,
 * etc., not to restrict admins to ASCII. Mirror this with the BE regex
 * in `api/src/services/viewColumn/create.ts`.
 */
const VALID_COLUMN_NAME_PATTERN = /^[\p{L}\p{N}\p{M} ]*$/u

export const getColumnRenameError = (value: string): string | undefined => {
  if (value.length > PER_COLUMN_RENAME_MAX_CHARS) {
    return `Please choose a name that is less than ${PER_COLUMN_RENAME_MAX_CHARS} characters`
  }
  if (value && !VALID_COLUMN_NAME_PATTERN.test(value)) {
    return 'Punctuation, symbols, and emoji are not allowed'
  }
  return undefined
}

const stopPopoverContentClickPropagation = (e: React.MouseEvent) => {
  e.stopPropagation()
}

type Props = {
  columnGeneratedId: string
  headerActions: ColumnHeaderActionsConfig
}

const TableHeaderActionsPopover = ({ columnGeneratedId, headerActions }: Props) => {
  const popoverState = useDisclosure()

  const canSort = headerActions.canSort(columnGeneratedId)

  const currentSortMode = canSort ? headerActions.getCurrentSortMode(columnGeneratedId) : null
  const currentOverride = headerActions.canRename
    ? headerActions.getCurrentDisplayLabelOverride(columnGeneratedId)
    : null
  const defaultLabel = headerActions.canRename
    ? headerActions.getDefaultDisplayLabel(columnGeneratedId)
    : ''

  const renameExpanded = useDisclosure({ initialOpen: !!currentOverride })
  const [renameValue, setRenameValue] = useState<string>(currentOverride ?? '')

  // Re-seed when the popover opens so reopening reflects the latest
  // override and a different column's popover doesn't leak the prior
  // column's text or expanded state. Keyed only on `popoverState.isOpen`
  // — re-running mid-open would clobber the user's in-progress typing.
  useEffect(() => {
    if (!popoverState.isOpen) {
      return
    }
    setRenameValue(currentOverride ?? '')
    if (currentOverride) {
      renameExpanded.open()
    } else {
      renameExpanded.close()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popoverState.isOpen])

  const renameError = getColumnRenameError(renameValue)

  const commitRename = useCallback(
    (value: string) => {
      if (getColumnRenameError(value)) {
        return
      }
      const trimmed = value.trim()
      headerActions.onRename(columnGeneratedId, trimmed || null)
    },
    [columnGeneratedId, headerActions]
  )

  const handleChevronClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      e.preventDefault()
      if (popoverState.isOpen) {
        popoverState.close()
      } else {
        popoverState.open()
      }
    },
    [popoverState]
  )

  const handleRenameValueChange = useCallback((value: string) => {
    setRenameValue(value)
  }, [])

  const handleRenameBlur = useCallback(() => {
    if (!renameError) {
      commitRename(renameValue)
    }
  }, [commitRename, renameError, renameValue])

  const handleSetSortAsc = useCallback(() => {
    headerActions.onSetSort(columnGeneratedId, 'Asc')
    popoverState.close()
  }, [columnGeneratedId, headerActions, popoverState])

  const handleSetSortDesc = useCallback(() => {
    headerActions.onSetSort(columnGeneratedId, 'Desc')
    popoverState.close()
  }, [columnGeneratedId, headerActions, popoverState])

  const handleClearSort = useCallback(() => {
    headerActions.onClearSort(columnGeneratedId)
    popoverState.close()
  }, [columnGeneratedId, headerActions, popoverState])

  const handleHide = useCallback(() => {
    headerActions.onHide(columnGeneratedId)
    popoverState.close()
  }, [columnGeneratedId, headerActions, popoverState])

  const handleToggleRenameExpanded = useCallback(() => {
    renameExpanded.toggle()
  }, [renameExpanded])

  // Stop key events bubbling out of the rename input so Enter / Esc
  // don't accidentally toggle the wrapping table header's sort or
  // close the entire drawer.
  const handleRenameKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()
        if (!renameError) {
          commitRename(renameValue)
          popoverState.close()
        }
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        renameExpanded.close()
        return
      }
      e.stopPropagation()
    },
    [commitRename, popoverState, renameError, renameValue, renameExpanded]
  )

  /**
   * Commit pending rename when the popover closes (e.g. outside click)
   * so in-progress edits aren't silently lost.
   */
  const handlePopoverClose = useCallback(() => {
    if (renameExpanded.isOpen && !renameError) {
      commitRename(renameValue)
    }
    popoverState.close()
  }, [commitRename, renameExpanded, popoverState, renameError, renameValue])

  return (
    <Popover isOpen={popoverState.isOpen} onOpen={popoverState.open} onClose={handlePopoverClose}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label="Column actions"
          className="flex h-5 w-5 cursor-pointer items-center justify-center rounded text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700"
          onClick={handleChevronClick}
        >
          <Icon icon="FaChevronDown" className="text-xs" />
        </button>
      </Popover.Trigger>
      <Popover.Content
        side="bottom"
        align="start"
        sideOffset={4}
        className="w-64 p-1"
        onClick={stopPopoverContentClickPropagation}
      >
        <VStack align="stretch" gap={0}>
          {canSort && (
            <>
              <ActionRow
                icon="FaArrowUp"
                label="Sort ascending"
                isActive={currentSortMode === 'Asc'}
                onClick={handleSetSortAsc}
              />
              <ActionRow
                icon="FaArrowDown"
                label="Sort descending"
                isActive={currentSortMode === 'Desc'}
                onClick={handleSetSortDesc}
              />
              {currentSortMode !== null && (
                <ActionRow
                  icon="FaXmark"
                  label="Clear sort"
                  isActive={false}
                  onClick={handleClearSort}
                />
              )}
            </>
          )}
          {headerActions.canRename && (
            <RenameSection
              defaultLabel={defaultLabel}
              errorMessage={renameError}
              isExpanded={renameExpanded.isOpen}
              onBlur={handleRenameBlur}
              onKeyDown={handleRenameKeyDown}
              onToggleExpanded={handleToggleRenameExpanded}
              onValueChange={handleRenameValueChange}
              value={renameValue}
            />
          )}
          {headerActions.canHide && (
            <ActionRow
              icon="FaEyeSlashSolid"
              label="Hide column"
              isActive={false}
              onClick={handleHide}
            />
          )}
        </VStack>
      </Popover.Content>
    </Popover>
  )
}

type ActionRowProps = {
  icon: IconKey
  isActive: boolean
  label: string
  onClick: () => void
}
// Raw <button> rather than govwell-ui Button because these rows need
// menu-item-style layout (full-width, text-left, icon/label/indicator)
// that fights Button's cva sizing variants. Menu.Item can't be used
// either — it requires a Radix Menu context and these live in a Popover.
const ActionRow = ({ icon, isActive, label, onClick }: ActionRowProps) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={twMerge(
        'flex w-full items-center gap-3 rounded px-2 py-2 text-left text-sm transition-colors hover:bg-neutral-100',
        isActive ? 'font-medium text-neutral-800' : 'text-neutral-700'
      )}
    >
      <span className="flex h-5 w-5 shrink-0 items-center justify-center text-sm text-neutral-500">
        <Icon icon={icon} />
      </span>
      <span className="flex-1 truncate">{label}</span>
      {isActive && (
        <span className="text-primary flex h-4 w-4 shrink-0 items-center justify-center text-xs">
          <Icon icon="FaCheckSolid" />
        </span>
      )}
    </button>
  )
}

type RenameSectionProps = {
  defaultLabel: string
  errorMessage: string | undefined
  isExpanded: boolean
  onBlur: () => void
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onToggleExpanded: () => void
  onValueChange: (value: string) => void
  value: string
}
/**
 * Collapsible inline rename row. Expands to a clearable input that
 * commits to MobX state on blur / Enter — no separate Save/Reset
 * buttons. The drawer's "Save Changes" button handles actual
 * persistence, matching the rest of the report editing UX.
 */
const RenameSection = ({
  defaultLabel,
  errorMessage,
  isExpanded,
  onBlur,
  onKeyDown,
  onToggleExpanded,
  onValueChange,
  value,
}: RenameSectionProps) => {
  return (
    <VStack align="stretch" gap={0}>
      <button
        type="button"
        onClick={onToggleExpanded}
        aria-expanded={isExpanded}
        className="flex w-full items-center gap-3 rounded px-2 py-2 text-left text-sm text-neutral-700 transition-colors hover:bg-neutral-100"
      >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center text-sm text-neutral-500">
          <Icon icon="FaPen" />
        </span>
        <span className="flex-1 truncate">Rename</span>
      </button>
      <div
        className={twMerge(
          'grid transition-[grid-template-rows] duration-150 ease-out',
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
        aria-hidden={!isExpanded}
      >
        <div className="overflow-hidden">
          <div className="px-2 pt-1 pb-2">
            <Input
              ariaLabel={`Rename column (default: ${defaultLabel})`}
              errorMessage={errorMessage}
              isInvalid={!!errorMessage}
              placeholder={defaultLabel}
              value={value}
              onValueChange={onValueChange}
              onBlur={onBlur}
              onFocus={(e) => e.target.select()}
              onKeyDown={onKeyDown}
              isClearable
              size="sm"
            />
          </div>
        </div>
      </div>
    </VStack>
  )
}

export default React.memo(TableHeaderActionsPopover)
